import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { API_BASE_URL } from '@config/constants';
import { ENDPOINTS } from '@api/endpoints';
import type { AuthState, LoginCredentials, LoginResponse, User, AuthTokens } from '@appTypes/auth.types';
import type { BootstrapSessionPayload } from '@modules/login/services/sessionBootstrap';

const initialState: AuthState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  sessionExpiredModalOpen: false,
  sessionBootstrapComplete: false,
};

export const loginAsync = createAsyncThunk<LoginResponse, LoginCredentials>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const { authService } = await import('@modules/login/services/authService');
      return await authService.login(credentials);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Login failed');
    }
  },
);

export const exchangeOAuthCodeAsync = createAsyncThunk<
  LoginResponse,
  { code: string; state: string | null },
  { rejectValue: string }
>('auth/exchangeOAuthCode', async ({ code, state }, { rejectWithValue }) => {
  try {
    const { authService } = await import('@modules/login/services/authService');
    return await authService.exchangeOAuthCode(code, state);
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Sign-in failed');
  }
});

export const logoutAsync = createAsyncThunk('auth/logout', async () => {
  const { authService } = await import('@modules/login/services/authService');
  await authService.logout();
});

interface BackendRefreshResponse {
  success: boolean;
  data: {
    access_token: string;
    refresh_token: string;
    token_type: string;
    expires_in: number;
  };
  message: string;
}

/**
 * Rotate the access + refresh token pair using the API's single-use refresh
 * endpoint. The new ``AuthTokens`` payload flows through ``setTokens``
 * (and redux-persist) so other tabs pick it up via the ``storage`` event.
 *
 * The body-less path matters for tabs that hydrated their session purely from
 * the httpOnly ``PR_REFRESH_TOKEN`` cookie via ``loadSessionFromCookies`` — they
 * have a logged-in user but no JS-readable refresh token. The API now reads the
 * refresh token from the request body when present and otherwise falls back to
 * the cookie, so sending an empty body still succeeds for those tabs.
 */
export const refreshSessionAsync = createAsyncThunk<
  AuthTokens,
  void,
  { state: { auth: AuthState }; rejectValue: string }
>('auth/refreshSession', async (_, { getState, rejectWithValue }) => {
  const refreshToken = getState().auth.tokens?.refreshToken;
  try {
    const resp = await fetch(`${API_BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(refreshToken ? { refresh_token: refreshToken } : {}),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return rejectWithValue(body.detail || body.message || 'Refresh failed');
    }
    const json = (await resp.json()) as BackendRefreshResponse;
    return {
      accessToken: json.data.access_token,
      refreshToken: json.data.refresh_token,
      expiresAt: Date.now() + json.data.expires_in * 1000,
    };
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Refresh failed');
  }
});

/** Restore session: cookie refresh → Bearer ``/me``, else cookie-only ``/me`` (see ``sessionBootstrap``). */
export const bootstrapSessionAsync = createAsyncThunk<
  BootstrapSessionPayload | null,
  void,
  { state: { auth: AuthState } }
>(
  'auth/bootstrapSession',
  async (_, { getState }) => {
    if (getState().auth.tokens?.accessToken) {
      return null;
    }
    const { restoreBootstrapSession } = await import('@modules/login/services/sessionBootstrap');
    if (getState().auth.tokens?.accessToken) {
      return null;
    }
    return restoreBootstrapSession(() => getState().auth.tokens?.accessToken);
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
    setTokens(state, action: PayloadAction<AuthTokens>) {
      state.tokens = action.payload;
    },
    clearAuth(state) {
      state.user = null;
      state.tokens = null;
      state.isAuthenticated = false;
      state.error = null;
      state.sessionExpiredModalOpen = false;
    },
    clearError(state) {
      state.error = null;
    },
    openSessionExpiredModal(state) {
      state.sessionExpiredModalOpen = true;
    },
    closeSessionExpiredModal(state) {
      state.sessionExpiredModalOpen = false;
    },
    /**
     * Apply a full auth snapshot from another tab's redux-persist write. Used by
     * ``SessionSyncListener`` so an idle ``/login`` tab does not apply only ``setTokens``
     * (which leaves ``isAuthenticated: false`` and causes a bogus ``clearAuth`` in the
     * tab that just logged in when this tab re-persists).
     */
    syncAuthFromPersistSnapshot(
      state,
      action: PayloadAction<{ user: User | null; tokens: AuthTokens | null; isAuthenticated: boolean }>,
    ) {
      state.user = action.payload.user;
      state.tokens = action.payload.tokens;
      state.isAuthenticated = action.payload.isAuthenticated;
      if (action.payload.isAuthenticated) {
        state.sessionExpiredModalOpen = false;
        state.error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.sessionExpiredModalOpen = false;
        // The user is provably authenticated now, so the session-bootstrap gate
        // must stay open. Otherwise a stray ``bootstrapSessionAsync`` re-run
        // (e.g. legacy SessionBootstrap re-fire on navigation) would briefly
        // flip ProtectedRoute back to LoadingFallback after the navigation
        // following login, producing the "double loading" flash.
        state.sessionBootstrapComplete = true;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(exchangeOAuthCodeAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(exchangeOAuthCodeAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.sessionExpiredModalOpen = false;
        state.error = null;
        // Mirror loginAsync.fulfilled: a successful SSO exchange means the
        // session is established, so ProtectedRoute must not gate the
        // post-callback navigation behind LoadingFallback.
        state.sessionBootstrapComplete = true;
      })
      .addCase(exchangeOAuthCodeAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Sign-in failed';
      })
      .addCase(logoutAsync.fulfilled, (state) => {
        state.user = null;
        state.tokens = null;
        state.isAuthenticated = false;
        state.error = null;
        state.sessionExpiredModalOpen = false;
      })
      .addCase(refreshSessionAsync.fulfilled, (state, action) => {
        state.tokens = action.payload;
        state.sessionExpiredModalOpen = false;
      })
      .addCase(bootstrapSessionAsync.pending, () => {
        // Intentionally a no-op. The gate is initialised to ``false`` and the
        // first ``.fulfilled`` / ``.rejected`` flips it to ``true``. Re-running
        // bootstrap (e.g. cross-tab refresh or future re-mounts) must NOT yank
        // the gate back to ``false`` mid-session, because doing so causes
        // ProtectedRoute to render LoadingFallback over already-authenticated
        // routes for one frame — the "second loading" flash users were seeing
        // immediately after a successful login or SSO callback navigation.
      })
      .addCase(bootstrapSessionAsync.fulfilled, (state, action) => {
        state.sessionBootstrapComplete = true;
        if (action.payload) {
          state.isAuthenticated = true;
          state.user = action.payload.user;
          state.tokens = action.payload.tokens;
          state.sessionExpiredModalOpen = false;
          state.error = null;
        }
      })
      .addCase(bootstrapSessionAsync.rejected, (state) => {
        state.sessionBootstrapComplete = true;
      });
  },
});

export const {
  setUser,
  setTokens,
  clearAuth,
  clearError,
  openSessionExpiredModal,
  closeSessionExpiredModal,
  syncAuthFromPersistSnapshot,
} = authSlice.actions;
export default authSlice.reducer;
